import React, { createContext, useContext, useState, useEffect } from "react";

// Currency data with conversion rates from EUR (base)
const CURRENCIES = {
  EUR: { symbol: "€", name: "Euro", rate: 1 },
  GBP: { symbol: "£", name: "British Pound", rate: 0.86 },
  USD: { symbol: "$", name: "US Dollar", rate: 1.08 },
  INR: { symbol: "₹", name: "Indian Rupee", rate: 90.50 },
  AUD: { symbol: "A$", name: "Australian Dollar", rate: 1.65 },
  CAD: { symbol: "C$", name: "Canadian Dollar", rate: 1.47 },
  JPY: { symbol: "¥", name: "Japanese Yen", rate: 162.50 },
  CNY: { symbol: "¥", name: "Chinese Yuan", rate: 7.85 },
  CHF: { symbol: "CHF", name: "Swiss Franc", rate: 0.95 },
  SGD: { symbol: "S$", name: "Singapore Dollar", rate: 1.45 },
  AED: { symbol: "د.إ", name: "UAE Dirham", rate: 3.97 },
  SAR: { symbol: "﷼", name: "Saudi Riyal", rate: 4.05 },
  MYR: { symbol: "RM", name: "Malaysian Ringgit", rate: 4.80 },
  THB: { symbol: "฿", name: "Thai Baht", rate: 37.50 },
  ZAR: { symbol: "R", name: "South African Rand", rate: 19.80 },
  BRL: { symbol: "R$", name: "Brazilian Real", rate: 5.30 },
  MXN: { symbol: "$", name: "Mexican Peso", rate: 18.50 },
  KRW: { symbol: "₩", name: "South Korean Won", rate: 1420 },
  NZD: { symbol: "NZ$", name: "New Zealand Dollar", rate: 1.78 },
  SEK: { symbol: "kr", name: "Swedish Krona", rate: 11.20 },
};

// Map country codes to currencies (for IP-based detection)
const COUNTRY_TO_CURRENCY = {
  // Europe (EUR)
  AT: "EUR", BE: "EUR", CY: "EUR", EE: "EUR", FI: "EUR", FR: "EUR", DE: "EUR",
  GR: "EUR", IE: "EUR", IT: "EUR", LV: "EUR", LT: "EUR", LU: "EUR", MT: "EUR",
  NL: "EUR", PT: "EUR", SK: "EUR", SI: "EUR", ES: "EUR",
  // UK
  GB: "GBP", UK: "GBP",
  // Americas
  US: "USD", CA: "CAD", MX: "MXN", BR: "BRL",
  // Asia Pacific
  IN: "INR", AU: "AUD", NZ: "NZD", JP: "JPY", CN: "CNY", KR: "KRW",
  SG: "SGD", MY: "MYR", TH: "THB",
  // Middle East
  AE: "AED", SA: "SAR",
  // Africa
  ZA: "ZAR",
  // Other Europe
  CH: "CHF", SE: "SEK",
};

// Map browser locales to currencies
const LOCALE_TO_CURRENCY = {
  "en-US": "USD",
  "en-GB": "GBP",
  "en-AU": "AUD",
  "en-CA": "CAD",
  "en-NZ": "NZD",
  "en-IN": "INR",
  "en-SG": "SGD",
  "en-ZA": "ZAR",
  "hi-IN": "INR",
  "hi": "INR",
  "ja-JP": "JPY",
  "ja": "JPY",
  "zh-CN": "CNY",
  "zh": "CNY",
  "ko-KR": "KRW",
  "ko": "KRW",
  "th-TH": "THB",
  "th": "THB",
  "ms-MY": "MYR",
  "ms": "MYR",
  "pt-BR": "BRL",
  "es-MX": "MXN",
  "sv-SE": "SEK",
  "sv": "SEK",
  "ar-AE": "AED",
  "ar-SA": "SAR",
  "de-CH": "CHF",
  "fr-CH": "CHF",
  "it-CH": "CHF",
};

// Base exam fee in EUR
export const BASE_EXAM_FEE_EUR = 99;

// Premium certification fee in EUR (AI Voiceover & Music Therapy)
export const PREMIUM_CERT_FEE_EUR = 199;

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState("EUR"); // Default to Euro
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Auto-detect currency on mount
  useEffect(() => {
    const detectCurrency = async () => {
      // Try to get from localStorage first (user preference)
      const saved = localStorage.getItem("preferredCurrency");
      if (saved && CURRENCIES[saved]) {
        setCurrency(saved);
        setIsAutoDetected(false);
        setIsLoading(false);
        return;
      }

      // Try IP-based geolocation for automatic country detection
      try {
        const response = await fetch("https://ipapi.co/json/", { 
          signal: AbortSignal.timeout(3000) // 3 second timeout
        });
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          if (countryCode && COUNTRY_TO_CURRENCY[countryCode]) {
            setCurrency(COUNTRY_TO_CURRENCY[countryCode]);
            setIsAutoDetected(true);
            setIsLoading(false);
            return;
          }
        }
      } catch (error) {
        // IP detection failed, fall through to browser locale detection
        console.log("IP-based currency detection unavailable, using locale fallback");
      }

      // Fallback to browser locale detection
      const browserLocale = navigator.language || navigator.userLanguage;
      
      // Try exact match first (e.g., "en-IN" -> INR)
      if (LOCALE_TO_CURRENCY[browserLocale]) {
        setCurrency(LOCALE_TO_CURRENCY[browserLocale]);
        setIsAutoDetected(true);
        setIsLoading(false);
        return;
      }
      
      // Try language code only (e.g., "hi" -> INR)
      const langCode = browserLocale.split("-")[0];
      if (LOCALE_TO_CURRENCY[langCode]) {
        setCurrency(LOCALE_TO_CURRENCY[langCode]);
        setIsAutoDetected(true);
        setIsLoading(false);
        return;
      }

      // Default to EUR if no match found
      setCurrency("EUR");
      setIsAutoDetected(false);
      setIsLoading(false);
    };

    detectCurrency();
  }, []);

  const changeCurrency = (newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      setIsAutoDetected(false);
      localStorage.setItem("preferredCurrency", newCurrency);
    }
  };

  const convertFromEUR = (amountEUR) => {
    const rate = CURRENCIES[currency]?.rate || 1;
    // Round up to next whole number
    return Math.ceil(amountEUR * rate);
  };

  const formatPrice = (amountEUR) => {
    const converted = convertFromEUR(amountEUR);
    const { symbol } = CURRENCIES[currency] || CURRENCIES.EUR;
    
    // Format as whole number without decimals
    return `${symbol}${converted.toLocaleString()}`;
  };

  const value = {
    currency,
    currencies: CURRENCIES,
    changeCurrency,
    convertFromEUR,
    formatPrice,
    isAutoDetected,
    isLoading,
    examFeeEUR: BASE_EXAM_FEE_EUR,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}

export { CURRENCIES };
